import { useState, useEffect, useCallback } from "react";
import { useTenantNavigation } from "@/hooks/useTenantNavigation";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { format, subDays, startOfWeek, startOfMonth } from "date-fns";
import {
  LogOut, ArrowLeft, Clock, AlertTriangle, Truck,
  TrendingUp, Target, Settings2, BarChart3, Activity, Gauge, Timer, Wrench, Download
} from "lucide-react";
import { Logo } from "@/components/Logo";
import { NotificationBell } from "@/components/NotificationBell";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, AreaChart, Area, Legend
} from "recharts";

const RANGE_OPTIONS = [
  { value: "today", label: "Today" },
  { value: "week", label: "This Week" },
  { value: "month", label: "This Month" },
  { value: "custom", label: "Custom Range" },
];

const CHART_COLORS = ["#6EE7B7", "#A7B0BA", "#F59E0B", "#EF4444", "#8B5CF6", "#06B6D4", "#EC4899", "#84CC16"];

function KpiCard({ label, value, unit, target, icon: Icon, color }) {
  const pct = target ? Math.min(100, Math.round((value / target) * 100)) : null;
  return (
    <Card className="border-border bg-card" data-testid={`kpi-card-${label.toLowerCase().replace(/[\s/]+/g, '-')}`}>
      <CardContent className="py-4 px-5">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs text-muted-foreground uppercase tracking-wider">{label}</span>
          <Icon className={`h-4 w-4 ${color}`} strokeWidth={1.5} />
        </div>
        <p className="kpi-value text-2xl">{typeof value === "number" ? value.toLocaleString(undefined, { maximumFractionDigits: 1 }) : value}<span className="text-sm text-muted-foreground font-normal ml-1">{unit}</span></p>
        {target != null && (
          <div className="mt-2">
            <div className="flex items-center justify-between text-xs mb-1">
              <span className="text-muted-foreground">Target: {target.toLocaleString()}</span>
              <span className={pct >= 100 ? "text-primary" : pct >= 75 ? "text-yellow-400" : "text-destructive"}>{pct}%</span>
            </div>
            <div className="h-1.5 bg-secondary rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${pct >= 100 ? "bg-primary" : pct >= 75 ? "bg-yellow-400" : "bg-destructive"}`}
                style={{ width: `${Math.min(pct, 100)}%` }}
              />
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ChartTooltipContent({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-card border border-border rounded-md px-3 py-2 shadow-lg">
      <p className="text-xs text-muted-foreground mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} className="text-xs font-medium" style={{ color: p.color }}>
          {p.name}: <span className="kpi-value">{typeof p.value === "number" ? p.value.toLocaleString(undefined, { maximumFractionDigits: 1 }) : p.value}</span>
        </p>
      ))}
    </div>
  );
}

export default function AnalyticsPage() {
  const { user, logout } = useAuth();
  const { navigate } = useTenantNavigation();

  const [lookups, setLookups] = useState({});
  const [range, setRange] = useState("month");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [siteFilter, setSiteFilter] = useState("all");
  const [contractorFilter, setContractorFilter] = useState("all");
  const [shiftFilter, setShiftFilter] = useState("all");

  const [kpis, setKpis] = useState(null);
  const [delayBreakdown, setDelayBreakdown] = useState([]);
  const [materialSummary, setMaterialSummary] = useState([]);
  const [dailyTrend, setDailyTrend] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  const [targetDialogOpen, setTargetDialogOpen] = useState(false);
  const [targetForm, setTargetForm] = useState({ kpi_key: "", target_value: "", label: "" });
  const [targets, setTargets] = useState([]);
  const [activeTab, setActiveTab] = useState("overview");
  const [equipDash, setEquipDash] = useState(null);
  const [equipLoading, setEquipLoading] = useState(false);

  const getDateRange = useCallback(() => {
    const today = format(new Date(), "yyyy-MM-dd");
    if (range === "today") return { date_from: today, date_to: today };
    if (range === "week") return { date_from: format(startOfWeek(new Date(), { weekStartsOn: 1 }), "yyyy-MM-dd"), date_to: today };
    if (range === "month") return { date_from: format(startOfMonth(new Date()), "yyyy-MM-dd"), date_to: today };
    if (range === "custom" && customFrom && customTo) return { date_from: customFrom, date_to: customTo };
    return { date_from: format(subDays(new Date(), 30), "yyyy-MM-dd"), date_to: today };
  }, [range, customFrom, customTo]);

  const buildParams = useCallback(() => {
    const { date_from, date_to } = getDateRange();
    const params = new URLSearchParams({ date_from, date_to });
    if (siteFilter !== "all") params.set("site_id", siteFilter);
    if (contractorFilter !== "all") params.set("contractor_id", contractorFilter);
    if (shiftFilter !== "all") params.set("shift", shiftFilter);
    return params.toString();
  }, [getDateRange, siteFilter, contractorFilter, shiftFilter]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const qs = buildParams();
      const promises = [
        api.get(`/analytics/kpis?${qs}`),
        api.get(`/analytics/delay-breakdown?${qs}`),
        api.get(`/analytics/material-summary?${qs}`),
        api.get(`/analytics/daily-trend?${qs}`),
      ];
      // Only admin can access targets endpoint
      if (user?.role === "admin") {
        promises.push(api.get("/admin/targets"));
      }
      const results = await Promise.all(promises);
      setKpis(results[0]);
      setDelayBreakdown(results[1]);
      setMaterialSummary(results[2]);
      setDailyTrend(results[3]);
      if (user?.role === "admin" && results[4]) {
        setTargets(results[4]);
      }
    } catch (err) {
      toast.error("Failed to load analytics");
    } finally {
      setLoading(false);
    }
  }, [buildParams, user?.role]);

  useEffect(() => {
    api.get("/lookups").then(setLookups).catch(() => {});
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const loadEquipDash = useCallback(async () => {
    setEquipLoading(true);
    try {
      const qs = buildParams();
      const data = await api.get(`/analytics/equipment-dashboard?${qs}`);
      setEquipDash(data);
    } catch {
      toast.error("Failed to load equipment dashboard");
    } finally {
      setEquipLoading(false);
    }
  }, [buildParams]);

  useEffect(() => {
    if (activeTab === "equipment") {
      loadEquipDash();
    }
  }, [activeTab, loadEquipDash]);

  const handleDownloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      const qs = buildParams();
      const { date_from, date_to } = getDateRange();
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/analytics/report-pdf?${qs}`,
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
      );
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || "Failed to generate PDF");
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `executive_summary_${date_from}_to_${date_to}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Report PDF downloaded");
    } catch (err) {
      toast.error(err.message || "Failed to generate PDF");
    } finally {
      setDownloadingPdf(false);
    }
  };


  const handleSaveTarget = async () => {
    if (!targetForm.kpi_key || !targetForm.target_value) {
      toast.error("KPI key and target value required");
      return;
    }
    try {
      await api.put(`/admin/targets/${targetForm.kpi_key}`, {
        target_value: parseFloat(targetForm.target_value),
        label: targetForm.label || targetForm.kpi_key,
      });
      toast.success("Target saved");
      setTargetDialogOpen(false);
      loadData();
    } catch (err) {
      toast.error("Failed to save target");
    }
  };

  const KPI_DEFS = [
    { key: "report_count", label: "Reports", icon: BarChart3, color: "text-primary", unit: "", val: kpis?.report_count },
    { key: "total_operating_hours", label: "Operating Hours", icon: Clock, color: "text-blue-400", unit: "hrs", val: kpis?.total_operating_hours },
    { key: "utilization_pct", label: "Utilization", icon: Activity, color: "text-emerald-400", unit: "%", val: kpis?.utilization_pct },
    { key: "productivity_cyd_hr", label: "CYD / Hour", icon: TrendingUp, color: "text-amber-400", unit: "cyd/hr", val: kpis?.productivity_cyd_hr },
    { key: "productivity_tons_hr", label: "Tons / Hour", icon: TrendingUp, color: "text-cyan-400", unit: "t/hr", val: kpis?.productivity_tons_hr },
    { key: "total_delay_hours", label: "Delay Hours", icon: AlertTriangle, color: "text-orange-400", unit: "hrs", val: kpis?.total_delay_hours },
    { key: "total_cyd", label: "Total CYD", icon: Truck, color: "text-yellow-400", unit: "cyd", val: kpis?.total_cyd },
    { key: "total_tons", label: "Total Tons", icon: Truck, color: "text-purple-400", unit: "t", val: kpis?.total_tons },
  ];

  const TARGET_OPTIONS = KPI_DEFS.map(k => ({ value: k.key, label: k.label }));

  return (
    <div className="min-h-screen" data-testid="analytics-page">
      <header className="border-b border-border px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Logo />
        </div>
        <div className="flex items-center gap-4">
          <NotificationBell />
          <span className="text-sm text-muted-foreground hidden sm:block">{user?.name || user?.email}</span>
          <Button variant="ghost" size="icon" onClick={logout} data-testid="logout-button">
            <LogOut className="h-4 w-4" strokeWidth={1.5} />
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        <Button variant="ghost" size="sm" className="mb-4 text-muted-foreground" onClick={() => navigate(user?.role === "viewer" ? "/reports" : "/admin")} data-testid="back-to-admin">
          <ArrowLeft className="h-4 w-4 mr-1" strokeWidth={1.5} /> {user?.role === "viewer" ? "Reports" : "Admin Panel"}
        </Button>

        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-headline text-2xl font-bold tracking-tight flex items-center gap-2">
              <BarChart3 className="h-6 w-6 text-primary" strokeWidth={1.5} />
              Analytics Dashboard
            </h1>
            <p className="text-muted-foreground text-sm mt-1">Production KPIs and performance metrics</p>
          </div>
          {user?.role === "admin" && (
            <Button variant="outline" size="sm" className="border-border" onClick={() => { setTargetForm({ kpi_key: "", target_value: "", label: "" }); setTargetDialogOpen(true); }} data-testid="set-targets-button">
              <Target className="h-4 w-4 mr-1" strokeWidth={1.5} /> Set Targets
            </Button>
          )}
        </div>

        {/* Filters */}
        <Card className="border-border bg-card mb-6" data-testid="analytics-filters">
          <CardContent className="py-4 px-5">
            <div className="flex flex-wrap items-end gap-4">
              <div className="space-y-1 min-w-[140px]">
                <Label className="text-xs text-muted-foreground">Time Range</Label>
                <Select value={range} onValueChange={setRange}>
                  <SelectTrigger data-testid="range-select" className="bg-background border-border h-9 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    {RANGE_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {range === "custom" && (
                <>
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">From</Label>
                    <Input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)} className="bg-background border-border h-9 text-sm font-mono w-[150px]" data-testid="custom-from" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">To</Label>
                    <Input type="date" value={customTo} onChange={e => setCustomTo(e.target.value)} className="bg-background border-border h-9 text-sm font-mono w-[150px]" data-testid="custom-to" />
                  </div>
                </>
              )}
              <div className="space-y-1 min-w-[160px]">
                <Label className="text-xs text-muted-foreground">Site</Label>
                <Select value={siteFilter} onValueChange={setSiteFilter}>
                  <SelectTrigger data-testid="site-filter" className="bg-background border-border h-9 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    <SelectItem value="all">All Sites</SelectItem>
                    {(lookups.sites || []).map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1 min-w-[160px]">
                <Label className="text-xs text-muted-foreground">Contractor</Label>
                <Select value={contractorFilter} onValueChange={setContractorFilter}>
                  <SelectTrigger data-testid="contractor-filter" className="bg-background border-border h-9 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    <SelectItem value="all">All Contractors</SelectItem>
                    {(lookups.contractors || []).map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1 min-w-[120px]">
                <Label className="text-xs text-muted-foreground">Shift</Label>
                <Select value={shiftFilter} onValueChange={setShiftFilter}>
                  <SelectTrigger data-testid="shift-filter" className="bg-background border-border h-9 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    <SelectItem value="all">All Shifts</SelectItem>
                    <SelectItem value="Day">Day</SelectItem>
                    <SelectItem value="Night">Night</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tab Navigation */}
        <div className="flex items-center gap-1 mb-6 border-b border-border" data-testid="analytics-tabs">
          <button
            onClick={() => setActiveTab("overview")}
            className={`px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px ${activeTab === "overview" ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}
            data-testid="tab-overview"
          >
            <BarChart3 className="h-4 w-4 inline mr-1.5 -mt-0.5" strokeWidth={1.5} />Overview
          </button>
          <button
            onClick={() => setActiveTab("equipment")}
            className={`px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px ${activeTab === "equipment" ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}
            data-testid="tab-equipment"
          >
            <Wrench className="h-4 w-4 inline mr-1.5 -mt-0.5" strokeWidth={1.5} />Equipment Dashboard
          </button>
          <div className="ml-auto -mb-px pb-1">
            <Button
              variant="outline"
              size="sm"
              onClick={handleDownloadPdf}
              disabled={downloadingPdf}
              className="gap-1.5 text-xs border-border hover:bg-primary/10 hover:text-primary"
              data-testid="download-report-pdf"
            >
              <Download className="h-3.5 w-3.5" strokeWidth={1.5} />
              {downloadingPdf ? "Generating..." : "Download Report"}
            </Button>
          </div>
        </div>

        {activeTab === "overview" && (
        <>
        {loading ? (
          <div className="flex items-center justify-center py-20 text-muted-foreground">Loading analytics...</div>
        ) : kpis?.report_count === 0 ? (
          <Card className="border-border bg-card">
            <CardContent className="py-16 text-center">
              <BarChart3 className="h-10 w-10 text-muted-foreground mx-auto mb-3" strokeWidth={1} />
              <p className="text-muted-foreground text-sm">No submitted reports found for the selected filters.</p>
              <p className="text-muted-foreground text-xs mt-1">Submit reports to see analytics data here.</p>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* KPI Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="kpi-cards-grid">
              {KPI_DEFS.map(k => (
                <KpiCard
                  key={k.key}
                  label={k.label}
                  value={k.val ?? 0}
                  unit={k.unit}
                  target={kpis?.targets?.[k.key] ?? null}
                  icon={k.icon}
                  color={k.color}
                />
              ))}
            </div>

            {/* Charts Row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
              {/* Daily Trend */}
              <Card className="border-border bg-card" data-testid="daily-trend-chart">
                <CardContent className="py-4 px-5">
                  <p className="text-sm font-medium mb-4 flex items-center gap-2">
                    <TrendingUp className="h-4 w-4 text-primary" strokeWidth={1.5} /> Daily Production Trend
                  </p>
                  {dailyTrend.length > 0 ? (
                    <ResponsiveContainer width="100%" height={240}>
                      <AreaChart data={dailyTrend}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(215 20% 20%)" />
                        <XAxis dataKey="date" tick={{ fill: "#A7B0BA", fontSize: 11 }} tickLine={false} axisLine={{ stroke: "hsl(215 20% 20%)" }} />
                        <YAxis tick={{ fill: "#A7B0BA", fontSize: 11 }} tickLine={false} axisLine={{ stroke: "hsl(215 20% 20%)" }} />
                        <Tooltip content={<ChartTooltipContent />} />
                        <Legend wrapperStyle={{ fontSize: 11, color: "#A7B0BA" }} />
                        <Area type="monotone" dataKey="op_hours" name="Op Hours" stroke="#6EE7B7" fill="#6EE7B7" fillOpacity={0.15} strokeWidth={2} />
                        <Area type="monotone" dataKey="delay_hours" name="Delay Hrs" stroke="#F59E0B" fill="#F59E0B" fillOpacity={0.1} strokeWidth={2} />
                      </AreaChart>
                    </ResponsiveContainer>
                  ) : (
                    <p className="text-center text-muted-foreground text-xs py-10">No trend data</p>
                  )}
                </CardContent>
              </Card>

              {/* Delay Breakdown */}
              <Card className="border-border bg-card" data-testid="delay-breakdown-chart">
                <CardContent className="py-4 px-5">
                  <p className="text-sm font-medium mb-4 flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4 text-orange-400" strokeWidth={1.5} /> Delay Breakdown
                  </p>
                  {delayBreakdown.length > 0 ? (
                    <ResponsiveContainer width="100%" height={240}>
                      <BarChart data={delayBreakdown} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(215 20% 20%)" />
                        <XAxis type="number" tick={{ fill: "#A7B0BA", fontSize: 11 }} tickLine={false} axisLine={{ stroke: "hsl(215 20% 20%)" }} />
                        <YAxis type="category" dataKey="code" tick={{ fill: "#A7B0BA", fontSize: 11 }} tickLine={false} axisLine={{ stroke: "hsl(215 20% 20%)" }} width={40} />
                        <Tooltip content={<ChartTooltipContent />} />
                        <Bar dataKey="hours" name="Hours" radius={[0, 4, 4, 0]}>
                          {delayBreakdown.map((_, i) => (
                            <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <p className="text-center text-muted-foreground text-xs py-10">No delay data</p>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Material & Loads Row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Material Summary */}
              <Card className="border-border bg-card" data-testid="material-summary-chart">
                <CardContent className="py-4 px-5">
                  <p className="text-sm font-medium mb-4 flex items-center gap-2">
                    <Truck className="h-4 w-4 text-amber-400" strokeWidth={1.5} /> Material Moved
                  </p>
                  {materialSummary.length > 0 ? (
                    <div className="flex items-center gap-6">
                      <ResponsiveContainer width="50%" height={200}>
                        <PieChart>
                          <Pie data={materialSummary} dataKey="tons" nameKey="material" cx="50%" cy="50%" outerRadius={80} innerRadius={40} paddingAngle={2}>
                            {materialSummary.map((_, i) => (
                              <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip content={<ChartTooltipContent />} />
                        </PieChart>
                      </ResponsiveContainer>
                      <div className="space-y-2 flex-1">
                        {materialSummary.map((m, i) => (
                          <div key={m.material} className="flex items-center justify-between text-xs">
                            <div className="flex items-center gap-2">
                              <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: CHART_COLORS[i % CHART_COLORS.length] }} />
                              <span>{m.material}</span>
                            </div>
                            <span className="kpi-value">
                              {m.tons > 0 && `${m.tons.toLocaleString()}t`}
                              {m.tons > 0 && m.cyd > 0 && " / "}
                              {m.cyd > 0 && `${m.cyd.toLocaleString()} cyd`}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <p className="text-center text-muted-foreground text-xs py-10">No material data</p>
                  )}
                </CardContent>
              </Card>

              {/* Loads per Day */}
              <Card className="border-border bg-card" data-testid="loads-trend-chart">
                <CardContent className="py-4 px-5">
                  <p className="text-sm font-medium mb-4 flex items-center gap-2">
                    <Activity className="h-4 w-4 text-cyan-400" strokeWidth={1.5} /> Daily Loads & Material
                  </p>
                  {dailyTrend.length > 0 ? (
                    <ResponsiveContainer width="100%" height={200}>
                      <BarChart data={dailyTrend}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(215 20% 20%)" />
                        <XAxis dataKey="date" tick={{ fill: "#A7B0BA", fontSize: 11 }} tickLine={false} axisLine={{ stroke: "hsl(215 20% 20%)" }} />
                        <YAxis tick={{ fill: "#A7B0BA", fontSize: 11 }} tickLine={false} axisLine={{ stroke: "hsl(215 20% 20%)" }} />
                        <Tooltip content={<ChartTooltipContent />} />
                        <Legend wrapperStyle={{ fontSize: 11, color: "#A7B0BA" }} />
                        <Bar dataKey="tons" name="Tons" fill="#8B5CF6" radius={[4, 4, 0, 0]} />
                        <Bar dataKey="cyd" name="CYD" fill="#06B6D4" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <p className="text-center text-muted-foreground text-xs py-10">No load data</p>
                  )}
                </CardContent>
              </Card>
            </div>
          </>
        )}
        </>
        )}

        {/* Equipment Dashboard Tab */}
        {activeTab === "equipment" && (
          equipLoading ? (
            <div className="flex items-center justify-center py-20 text-muted-foreground">Loading equipment data...</div>
          ) : !equipDash ? (
            <Card className="border-border bg-card">
              <CardContent className="py-16 text-center">
                <Wrench className="h-10 w-10 text-muted-foreground mx-auto mb-3" strokeWidth={1} />
                <p className="text-muted-foreground text-sm">No equipment data available for the selected filters.</p>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Fleet Summary KPIs */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="fleet-summary-cards">
                <KpiCard label="Active Equipment" value={equipDash.fleet_summary.active_equipment} unit={`/ ${equipDash.fleet_summary.total_equipment}`} icon={Wrench} color="text-primary" />
                <KpiCard label="Fleet Utilization" value={equipDash.fleet_summary.fleet_utilization} unit="%" icon={Gauge} color="text-emerald-400" />
                <KpiCard label="Total Op Hours" value={equipDash.fleet_summary.total_op_hours} unit="hrs" icon={Clock} color="text-blue-400" />
                <KpiCard label="Total Delay Hours" value={equipDash.fleet_summary.total_delay_hours} unit="hrs" icon={AlertTriangle} color="text-orange-400" />
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
                {/* Activity Breakdown Chart */}
                <Card className="border-border bg-card" data-testid="activity-breakdown-chart">
                  <CardContent className="py-4 px-5">
                    <p className="text-sm font-medium mb-4 flex items-center gap-2">
                      <Activity className="h-4 w-4 text-primary" strokeWidth={1.5} /> Hours by Activity
                    </p>
                    {equipDash.activity_breakdown.length > 0 ? (
                      <ResponsiveContainer width="100%" height={240}>
                        <BarChart data={equipDash.activity_breakdown} layout="vertical">
                          <CartesianGrid strokeDasharray="3 3" stroke="hsl(215 20% 20%)" />
                          <XAxis type="number" tick={{ fill: "#A7B0BA", fontSize: 11 }} tickLine={false} axisLine={{ stroke: "hsl(215 20% 20%)" }} />
                          <YAxis type="category" dataKey="activity" tick={{ fill: "#A7B0BA", fontSize: 10 }} tickLine={false} axisLine={{ stroke: "hsl(215 20% 20%)" }} width={110} />
                          <Tooltip content={<ChartTooltipContent />} />
                          <Bar dataKey="hours" name="Hours" radius={[0, 4, 4, 0]}>
                            {equipDash.activity_breakdown.map((_, i) => (
                              <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <p className="text-center text-muted-foreground text-xs py-10">No activity data. Assign activities when logging equipment hours.</p>
                    )}
                  </CardContent>
                </Card>

                {/* Cycle Time Chart */}
                <Card className="border-border bg-card" data-testid="cycle-time-chart">
                  <CardContent className="py-4 px-5">
                    <p className="text-sm font-medium mb-4 flex items-center gap-2">
                      <Timer className="h-4 w-4 text-cyan-400" strokeWidth={1.5} /> Avg Cycle Time (Material Trips)
                    </p>
                    {equipDash.cycle_times.length > 0 ? (
                      <ResponsiveContainer width="100%" height={240}>
                        <BarChart data={equipDash.cycle_times}>
                          <CartesianGrid strokeDasharray="3 3" stroke="hsl(215 20% 20%)" />
                          <XAxis dataKey="unit_id" tick={{ fill: "#A7B0BA", fontSize: 11 }} tickLine={false} axisLine={{ stroke: "hsl(215 20% 20%)" }} />
                          <YAxis tick={{ fill: "#A7B0BA", fontSize: 11 }} tickLine={false} axisLine={{ stroke: "hsl(215 20% 20%)" }} label={{ value: "mins", angle: -90, position: "insideLeft", fill: "#A7B0BA", fontSize: 10 }} />
                          <Tooltip content={<ChartTooltipContent />} />
                          <Bar dataKey="avg_cycle_mins" name="Avg Cycle (min)" fill="#06B6D4" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <p className="text-center text-muted-foreground text-xs py-10">No cycle time data. Log material trips with start/end times.</p>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Equipment Utilization Table */}
              <Card className="border-border bg-card mb-6" data-testid="equipment-utilization-table">
                <CardContent className="py-4 px-5">
                  <p className="text-sm font-medium mb-4 flex items-center gap-2">
                    <Wrench className="h-4 w-4 text-primary" strokeWidth={1.5} /> Equipment Utilization
                  </p>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="border-border hover:bg-transparent">
                          <TableHead className="text-muted-foreground text-xs uppercase">Equipment</TableHead>
                          <TableHead className="text-muted-foreground text-xs uppercase">Type</TableHead>
                          <TableHead className="text-muted-foreground text-xs uppercase">Op Hours</TableHead>
                          <TableHead className="text-muted-foreground text-xs uppercase">Delay Hours</TableHead>
                          <TableHead className="text-muted-foreground text-xs uppercase">Utilization</TableHead>
                          <TableHead className="text-muted-foreground text-xs uppercase">Shifts</TableHead>
                          <TableHead className="text-muted-foreground text-xs uppercase">Top Activity</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {equipDash.equipment_list.map((eq) => (
                          <TableRow key={eq.id} className="border-border" data-testid={`equip-row-${eq.id}`}>
                            <TableCell className="font-medium text-sm">{eq.unit_id}</TableCell>
                            <TableCell className="text-sm text-muted-foreground">{eq.equipment_type || "-"}</TableCell>
                            <TableCell className="kpi-value text-sm">{eq.op_hours.toFixed(1)}h</TableCell>
                            <TableCell className="text-sm text-orange-400">{eq.delay_hours.toFixed(1)}h</TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <div className="w-16 h-1.5 bg-secondary rounded-full overflow-hidden">
                                  <div className={`h-full rounded-full ${eq.utilization >= 80 ? "bg-primary" : eq.utilization >= 50 ? "bg-yellow-400" : "bg-destructive"}`} style={{ width: `${Math.min(eq.utilization, 100)}%` }} />
                                </div>
                                <span className={`text-xs font-mono ${eq.utilization >= 80 ? "text-primary" : eq.utilization >= 50 ? "text-yellow-400" : "text-destructive"}`}>{eq.utilization}%</span>
                              </div>
                            </TableCell>
                            <TableCell className="text-sm font-mono">{eq.shifts}</TableCell>
                            <TableCell>{eq.top_activity ? <Badge variant="outline" className="text-xs border-border">{eq.top_activity}</Badge> : <span className="text-muted-foreground text-xs">—</span>}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>

              {/* Cycle Time Detail Table */}
              {equipDash.cycle_times.length > 0 && (
                <Card className="border-border bg-card" data-testid="cycle-time-table">
                  <CardContent className="py-4 px-5">
                    <p className="text-sm font-medium mb-4 flex items-center gap-2">
                      <Timer className="h-4 w-4 text-cyan-400" strokeWidth={1.5} /> Cycle Time Detail (Material Hauling)
                    </p>
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow className="border-border hover:bg-transparent">
                            <TableHead className="text-muted-foreground text-xs uppercase">Equipment</TableHead>
                            <TableHead className="text-muted-foreground text-xs uppercase">Type</TableHead>
                            <TableHead className="text-muted-foreground text-xs uppercase">Avg Cycle</TableHead>
                            <TableHead className="text-muted-foreground text-xs uppercase">Min</TableHead>
                            <TableHead className="text-muted-foreground text-xs uppercase">Max</TableHead>
                            <TableHead className="text-muted-foreground text-xs uppercase">Trips</TableHead>
                            <TableHead className="text-muted-foreground text-xs uppercase">Total Loads</TableHead>
                            <TableHead className="text-muted-foreground text-xs uppercase">Total Qty</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {equipDash.cycle_times.map((ct) => (
                            <TableRow key={ct.equipment_id} className="border-border" data-testid={`cycle-row-${ct.equipment_id}`}>
                              <TableCell className="font-medium text-sm">{ct.unit_id}</TableCell>
                              <TableCell className="text-sm text-muted-foreground">{ct.equipment_type || "-"}</TableCell>
                              <TableCell className="kpi-value text-sm">{ct.avg_cycle_mins} min</TableCell>
                              <TableCell className="text-sm font-mono text-primary">{ct.min_cycle_mins} min</TableCell>
                              <TableCell className="text-sm font-mono text-orange-400">{ct.max_cycle_mins} min</TableCell>
                              <TableCell className="text-sm font-mono">{ct.total_trips}</TableCell>
                              <TableCell className="text-sm font-mono">{ct.total_loads}</TableCell>
                              <TableCell className="kpi-value text-sm">{ct.total_qty.toLocaleString()}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          )
        )}

        {/* Target Dialog */}
        <Dialog open={targetDialogOpen} onOpenChange={setTargetDialogOpen}>
          <DialogContent className="bg-card border-border max-w-md" data-testid="target-dialog">
            <DialogHeader>
              <DialogTitle className="font-headline flex items-center gap-2"><Target className="h-4 w-4 text-primary" /> Set KPI Target</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <Label className="text-sm text-muted-foreground">KPI *</Label>
                <Select value={targetForm.kpi_key} onValueChange={v => setTargetForm({ ...targetForm, kpi_key: v, label: TARGET_OPTIONS.find(o => o.value === v)?.label || v })}>
                  <SelectTrigger data-testid="target-kpi-select" className="bg-background border-border"><SelectValue placeholder="Select KPI" /></SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    {TARGET_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-sm text-muted-foreground">Target Value *</Label>
                <Input data-testid="target-value-input" type="number" step="0.01" value={targetForm.target_value} onChange={e => setTargetForm({ ...targetForm, target_value: e.target.value })} className="bg-background border-border font-mono" placeholder="e.g. 100" />
              </div>
              {/* Show existing targets */}
              {targets.length > 0 && (
                <div className="border-t border-border pt-3">
                  <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Current Targets</p>
                  <div className="space-y-1">
                    {targets.map(t => (
                      <div key={t.kpi_key} className="flex items-center justify-between text-xs py-1">
                        <span>{t.label || t.kpi_key}</span>
                        <span className="kpi-value">{t.target_value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setTargetDialogOpen(false)} className="border-border">Cancel</Button>
              <Button onClick={handleSaveTarget} data-testid="save-target-button" className="font-headline font-semibold">Save Target</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
